/**
 * AdminRegions — Region Management landing (BON-153). Admin only.
 * Map-forward view from the design's `region-map-hierarchy.jsx`: a venue
 * map dominant on the left, with the hierarchy tree + selected-region detail
 * in the right rail (New region / Backfill review actions above the tree).
 * Wired to the live region API; the builder / reassignment / venue-exception
 * surfaces are separate.
 */
import { Suspense, lazy, useMemo, useState } from 'react';

import { BucketError, BucketLoading, SectionCard } from '../../regional/BucketStates';

import { RegionCreateModal } from './RegionCreateModal';
import { buildColorMap } from './RegionHierarchyTree';
import { RegionLandingRail } from './RegionLandingRail';
import { flattenById, subtreeIds } from './regionRoot';
import {
  useRegionTree,
  useMapVenues,
  useRegionBoundaries,
  useRegionDetail,
  useMoveRegion,
} from './regionsQueries';

// Lazy-loaded so mapbox-gl stays out of the synchronous module graph (it can't
// initialize under the test environment) — mirrors VenueMapPage's lazy VenueMap.
const RegionVenueMap = lazy(() =>
  import('./RegionVenueMap').then((m) => ({ default: m.RegionVenueMap })),
);

export default function AdminRegions() {
  const treeQ = useRegionTree();
  const venuesQ = useMapVenues();
  const boundariesQ = useRegionBoundaries();
  const move = useMoveRegion();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const detailQ = useRegionDetail(selectedId);

  const regions = useMemo(() => treeQ.data ?? [], [treeQ.data]);
  const colorMap = useMemo(() => buildColorMap(regions), [regions]);
  // The selected region plus its subregions — venues in any of these count as
  // members, so the map highlights the whole subtree, not just direct venues.
  const memberRegionIds = useMemo(
    () => (selectedId ? subtreeIds(flattenById(regions), selectedId) : null),
    [regions, selectedId],
  );

  if (treeQ.isLoading) return <BucketLoading />;
  if (treeQ.isError) return <BucketError error={treeQ.error} />;

  const venues = venuesQ.data ?? [];
  const boundaries = boundariesQ.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, minHeight: 0 }}>
      <div
        style={{
          flex: 1,
          minHeight: 0,
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1.7fr) minmax(0, 1fr)',
          gap: 14,
        }}
      >
        <SectionCard title="Territory">
          <div style={{ height: 460 }}>
            <Suspense fallback={<div style={{ height: '100%' }} />}>
              <RegionVenueMap
                venues={venues}
                boundaries={boundaries}
                colorMap={colorMap}
                selectedRegionId={selectedId}
                memberRegionIds={memberRegionIds}
              />
            </Suspense>
          </div>
        </SectionCard>

        <RegionLandingRail
          regions={regions}
          colorMap={colorMap}
          selectedId={selectedId}
          onSelect={setSelectedId}
          onReparent={(id, parentId) => move.mutate({ id, parentId })}
          onNewRegion={() => setCreating(true)}
          detailQuery={detailQ}
        />
      </div>

      {creating && (
        <RegionCreateModal
          regions={regions}
          onClose={() => setCreating(false)}
          onCreated={(id) => {
            setSelectedId(id);
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}
